import { CloudArrowUpIcon, EnvelopeIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { useTheme } from '../contexts/ThemeContext';
import { useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import apiService, { GymSetting } from '../services/api';

const GymSettingsPage: React.FC = () => {
  const { refreshUser } = useAuth();
  const { theme } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [settings, setSettings] = useState<GymSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEmail, setGoogleEmail] = useState<string | null>(null);
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false);
  const [isDisconnectingGoogle, setIsDisconnectingGoogle] = useState(false);

  // Form states
  const [gymForm, setGymForm] = useState({
    name: '',
    address: '',
    phone: '',
    email: '',
    description: '',
    logo_url: ''
  });

  usePageTitle('Gym Settings');

  useEffect(() => {
    loadSettings();
    loadGoogleStatus();

    // Check for Google connection callback
    const googleConnected = searchParams.get('google_connected');
    const error = searchParams.get('error');
    
    if (googleConnected === 'true') {
      setMessage({
        type: 'success',
        text: 'Google account connected successfully!',
      });
      loadGoogleStatus();
      // Remove query parameter
      searchParams.delete('google_connected');
      setSearchParams(searchParams, { replace: true });
    } else if (error === 'oauth_denied') {
      setMessage({
        type: 'error',
        text: 'Google connection was cancelled.',
      });
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadGoogleStatus = async () => {
    try {
      const status = await apiService.getGoogleConnectionStatus();
      setGoogleConnected(status.connected);
      setGoogleEmail(status.email);
    } catch (error) {
      console.error('Failed to load Google connection status:', error);
    }
  };

  const loadSettings = async () => {
    try {
      const [settingsData, gymInfo] = await Promise.all([
        apiService.getGymSettings(),
        apiService.getGymInfo()
      ]);
      
      setSettings(settingsData);
      setGymForm({
        name: gymInfo.name || '',
        address: gymInfo.address || '',
        phone: gymInfo.phone || '',
        email: gymInfo.email || '',
        description: gymInfo.description || '',
        logo_url: gymInfo.logo_url || ''
      });
    } catch (error) {
      console.error('Failed to load settings:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load gym settings',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setGymForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveGymInfo = async () => {
    setIsSaving(true);
    setMessage(null);

    try {
      await apiService.updateGymInfo(gymForm);
      setMessage({
        type: 'success',
        text: 'Gym information updated successfully!',
      });
      
      // Refresh user data to update gym info
      await refreshUser();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Failed to update gym information',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSettingChange = async (settingId: number, value: string) => {
    try {
      await apiService.updateGymSetting(settingId, value);
      setMessage({
        type: 'success',
        text: 'Setting updated successfully!',
      });
      
      // Reload settings to get updated values
      loadSettings();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Failed to update setting',
      });
    }
  };

  const handleConnectGoogle = async () => {
    try {
      setIsConnectingGoogle(true);
      setMessage(null);
      const { authUrl } = await apiService.getGoogleEmailConnectUrl();
      // Redirect to Google OAuth
      window.location.href = authUrl;
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Failed to initiate Google connection',
      });
      setIsConnectingGoogle(false);
    }
  };

  const handleDisconnectGoogle = async () => {
    if (!window.confirm('Are you sure you want to disconnect your Google account? Emails will be sent from the default system email.')) {
      return;
    }

    try {
      setIsDisconnectingGoogle(true);
      setMessage(null);
      await apiService.disconnectGoogleAccount();
      setMessage({
        type: 'success',
        text: 'Google account disconnected successfully!',
      });
      await loadGoogleStatus();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Failed to disconnect Google account',
      });
    } finally {
      setIsDisconnectingGoogle(false);
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setMessage({
        type: 'error',
        text: 'Please select a valid image file',
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({
        type: 'error',
        text: 'File size must be less than 5MB',
      });
      return;
    }

    setIsUploading(true);
    setMessage(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('logo', file);

      // Upload the file
      const response = await apiService.uploadGymLogo(formData);
      
      // Update the form with the new logo URL
      setGymForm(prev => ({
        ...prev,
        logo_url: response.logo_url
      }));

      setMessage({
        type: 'success',
        text: 'Logo uploaded successfully!',
      });

      // Refresh user data to update the logo in the header
      await refreshUser();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || error.message || 'Failed to upload logo',
      });
    } finally {
      setIsUploading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className={clsx(
          "text-4xl font-bold bg-clip-text text-transparent",
          theme === 'dark'
            ? "bg-gradient-to-r from-white to-gray-300"
            : "bg-gradient-to-r from-gray-900 to-gray-700"
        )}>
          Gym Settings
        </h1>
        <p className={clsx(
          "mt-3 text-lg transition-colors",
          theme === 'dark' ? "text-gray-300" : "text-gray-600"
        )}>
          Manage your gym's information and configuration
        </p>
      </div>

      {/* Message */}
      {message && (
        <div className={`rounded-xl p-4 backdrop-blur-sm border ${
          message.type === 'success' 
            ? 'bg-green-500/20 border-green-400/30 text-green-200' 
            : 'bg-red-500/20 border-red-400/30 text-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* General Settings Section */}
      <div className={clsx(
        "backdrop-blur-lg rounded-2xl shadow-2xl border p-8 transition-colors",
        theme === 'dark'
          ? "bg-white/10 border-white/20"
          : "bg-white/80 border-gray-200/50"
      )}>
        <div className="flex items-center justify-between mb-6">
          <h2 className={clsx(
            "text-2xl font-bold transition-colors",
            theme === 'dark' ? "text-white" : "text-gray-900"
          )}>General Settings</h2>
          <button
            onClick={handleSaveGymInfo}
            disabled={isSaving}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-medium py-2 px-6 rounded-xl transition-all duration-200 transform hover:scale-105 disabled:scale-100 disabled:cursor-not-allowed shadow-lg"
          >
            {isSaving ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </div>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>

        {/* Gym Information Subsection */}
        <div className="mb-8">
          <h3 className={clsx(
            "text-xl font-semibold mb-4 flex items-center transition-colors",
            theme === 'dark' ? "text-white" : "text-gray-900"
          )}>
            <div className="w-1 h-6 bg-indigo-500 rounded-full mr-3"></div>
            Gym Information
          </h3>
          <div className="space-y-6">
            {/* Basic Information Row */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>Gym Name *</label>
                <input
                  type="text"
                  value={gymForm.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={clsx(
                    "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                  )}
                  placeholder="Enter gym name"
                />
              </div>
              
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>Phone</label>
                <input
                  type="tel"
                  value={gymForm.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={clsx(
                    "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                  )}
                  placeholder="Enter phone number"
                />
              </div>
            </div>

            {/* Contact Information Row */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>Email</label>
                <input
                  type="email"
                  value={gymForm.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={clsx(
                    "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                  )}
                  placeholder="Enter email address"
                />
              </div>

              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>Address</label>
                <input
                  type="text"
                  value={gymForm.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className={clsx(
                    "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                    theme === 'dark'
                      ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                  )}
                  placeholder="Enter gym address"
                />
              </div>
            </div>

            {/* Logo Section - Split Layout */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Logo Controls */}
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>Logo</label>
                <div className="space-y-3">
                  {/* Upload Button */}
                  <div className="relative">
                    <input
                      type="file"
                      id="logo-upload"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={isUploading}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                    />
                    <label
                      htmlFor="logo-upload"
                      className={clsx(
                        `flex items-center justify-center w-full px-4 py-3 backdrop-blur-sm border rounded-xl transition-all duration-200 cursor-pointer disabled:cursor-not-allowed ${
                          isUploading ? 'opacity-50' : ''
                        }`,
                        theme === 'dark'
                          ? "bg-white/10 border-white/20 text-white hover:bg-white/20"
                          : "bg-white border-gray-300 text-gray-900 hover:bg-gray-50"
                      )}
                    >
                      {isUploading ? (
                        <div className="flex items-center">
                          <div className={clsx(
                            "animate-spin rounded-full h-4 w-4 border-b-2 mr-2",
                            theme === 'dark' ? "border-white" : "border-gray-900"
                          )}></div>
                          Uploading...
                        </div>
                      ) : (
                        <div className="flex items-center">
                          <CloudArrowUpIcon className="h-5 w-5 mr-2" />
                          Upload Logo
                        </div>
                      )}
                    </label>
                  </div>
                  
                  {/* Manual URL Input */}
                  <div>
                    <label className={clsx(
                      "block text-xs font-medium mb-1 transition-colors",
                      theme === 'dark' ? "text-gray-400" : "text-gray-500"
                    )}>Or enter logo URL manually:</label>
                    <input
                      type="url"
                      value={gymForm.logo_url}
                      onChange={(e) => handleInputChange('logo_url', e.target.value)}
                      className={clsx(
                        "w-full px-3 py-2 backdrop-blur-sm border rounded-lg focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200 text-sm",
                        theme === 'dark'
                          ? "bg-white/5 border-white/10 text-white placeholder-gray-400 focus:bg-white/10"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                      )}
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                </div>
              </div>

              {/* Logo Preview */}
              <div>
                <label className={clsx(
                  "block text-sm font-medium mb-2 transition-colors",
                  theme === 'dark' ? "text-gray-300" : "text-gray-700"
                )}>Preview</label>
                <div className={clsx(
                  "aspect-square w-full max-w-[200px] mx-auto flex items-center justify-center p-4 rounded-xl border transition-colors",
                  theme === 'dark'
                    ? "bg-white/5 border-white/10"
                    : "bg-gray-50 border-gray-200"
                )}>
                  {gymForm.logo_url ? (
                    <div className="text-center">
                      <img 
                        src={gymForm.logo_url} 
                        alt="Gym Logo Preview" 
                        className="w-16 h-16 object-cover rounded-lg mx-auto mb-2"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <p className={clsx(
                        "text-sm transition-colors",
                        theme === 'dark' ? "text-gray-300" : "text-gray-600"
                      )}>Current logo</p>
                    </div>
                  ) : (
                    <div className={clsx(
                      "text-center transition-colors",
                      theme === 'dark' ? "text-gray-400" : "text-gray-500"
                    )}>
                      <div className={clsx(
                        "w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-2 transition-colors",
                        theme === 'dark' ? "bg-white/5" : "bg-gray-100"
                      )}>
                        <CloudArrowUpIcon className="h-8 w-8 opacity-50" />
                      </div>
                      <p className="text-sm">No logo uploaded</p>
                    </div>
                  )}
                </div>
                {gymForm.logo_url && (
                  <p className={clsx(
                    "text-xs truncate max-w-[200px] mx-auto mt-2 text-center transition-colors",
                    theme === 'dark' ? "text-gray-400" : "text-gray-500"
                  )}>{gymForm.logo_url}</p>
                )}
              </div>
            </div>

            {/* Description - Full Width */}
            <div>
              <label className={clsx(
                "block text-sm font-medium mb-2 transition-colors",
                theme === 'dark' ? "text-gray-300" : "text-gray-700"
              )}>Description</label>
              <textarea
                value={gymForm.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className={clsx(
                  "w-full px-4 py-3 backdrop-blur-sm border rounded-xl focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200",
                  theme === 'dark'
                    ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                )}
                placeholder="Enter gym description"
              />
            </div>
          </div>
        </div>

        {/* Operational Settings Subsection */}
        {settings.length > 0 && (
          <div>
            <h3 className={clsx(
              "text-xl font-semibold mb-4 flex items-center transition-colors",
              theme === 'dark' ? "text-white" : "text-gray-900"
            )}>
              <div className="w-1 h-6 bg-purple-500 rounded-full mr-3"></div>
              Operational Settings
            </h3>
            <div className="space-y-4">
              {settings.map((setting) => (
                <div key={setting.id} className={clsx(
                  "flex items-center justify-between p-4 rounded-xl border transition-colors",
                  theme === 'dark'
                    ? "bg-white/5 border-white/10"
                    : "bg-gray-50 border-gray-200"
                )}>
                  <div className="flex-1">
                    <h4 className={clsx(
                      "text-lg font-medium transition-colors",
                      theme === 'dark' ? "text-white" : "text-gray-900"
                    )}>{setting.name}</h4>
                    <p className={clsx(
                      "text-sm mt-1 transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-600"
                    )}>{setting.description}</p>
                  </div>
                  <div className="flex items-center space-x-4">
                    <input
                      type="text"
                      value={setting.value || ''}
                      onChange={(e) => handleSettingChange(setting.id, e.target.value)}
                      className={clsx(
                        "px-3 py-2 backdrop-blur-sm border rounded-lg focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-200 w-48",
                        theme === 'dark'
                          ? "bg-white/10 border-white/20 text-white placeholder-gray-300 focus:bg-white/20"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:bg-white"
                      )}
                      placeholder="Enter value"
                    />
                    <span className={clsx(
                      "text-sm transition-colors",
                      theme === 'dark' ? "text-gray-400" : "text-gray-500"
                    )}>{setting.unit || ''}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Google Email Integration Section */}
      <div className={clsx(
        "backdrop-blur-lg rounded-2xl shadow-2xl border p-8 transition-colors",
        theme === 'dark'
          ? "bg-white/10 border-white/20"
          : "bg-white/80 border-gray-200/50"
      )}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className={clsx(
              "text-2xl font-bold transition-colors flex items-center",
              theme === 'dark' ? "text-white" : "text-gray-900"
            )}>
              <EnvelopeIcon className="h-6 w-6 mr-3" />
              Google Email Integration
            </h2>
            <p className={clsx(
              "text-sm mt-2 transition-colors",
              theme === 'dark' ? "text-gray-300" : "text-gray-600"
            )}>
              Connect your Google Workspace account to send emails from your gym's email address
            </p>
          </div>
        </div>

        <div className={clsx(
          "p-6 rounded-xl border transition-colors",
          theme === 'dark'
            ? "bg-white/5 border-white/10"
            : "bg-gray-50 border-gray-200"
        )}>
          {googleConnected ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-6 w-6 text-green-500 mr-3" />
                  <div>
                    <p className={clsx(
                      "font-semibold transition-colors",
                      theme === 'dark' ? "text-white" : "text-gray-900"
                    )}>
                      Google Account Connected
                    </p>
                    <p className={clsx(
                      "text-sm transition-colors",
                      theme === 'dark' ? "text-gray-300" : "text-gray-600"
                    )}>
                      {googleEmail || 'Connected'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleDisconnectGoogle}
                  disabled={isDisconnectingGoogle}
                  className={clsx(
                    "px-4 py-2 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed",
                    theme === 'dark'
                      ? "bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
                      : "bg-red-50 text-red-600 border border-red-200 hover:bg-red-100"
                  )}
                >
                  {isDisconnectingGoogle ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                      Disconnecting...
                    </div>
                  ) : (
                    'Disconnect'
                  )}
                </button>
              </div>
              <p className={clsx(
                "text-sm transition-colors",
                theme === 'dark' ? "text-gray-400" : "text-gray-500"
              )}>
                All emails sent from your gym will now be sent from your Google account instead of the default system email.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center">
                <XCircleIcon className="h-6 w-6 text-gray-400 mr-3" />
                <div>
                  <p className={clsx(
                    "font-semibold transition-colors",
                    theme === 'dark' ? "text-white" : "text-gray-900"
                  )}>
                    Google Account Not Connected
                  </p>
                  <p className={clsx(
                    "text-sm transition-colors",
                    theme === 'dark' ? "text-gray-300" : "text-gray-600"
                  )}>
                    Connect your Google Workspace account to send emails from your gym's email address
                  </p>
                </div>
              </div>
              <button
                onClick={handleConnectGoogle}
                disabled={isConnectingGoogle}
                className={clsx(
                  "flex items-center justify-center gap-3 px-6 py-3 rounded-lg font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed",
                  theme === 'dark'
                    ? "bg-white/10 text-white border border-white/20 hover:bg-white/20"
                    : "bg-white text-gray-900 border border-gray-300 hover:bg-gray-50"
                )}
              >
                {isConnectingGoogle ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
                    Connecting...
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Connect Google Account
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GymSettingsPage;